import { AnvaPossiblePackage, AnvaQuote } from '@flows/src/types/anva';
import { create } from 'zustand';

export interface Package extends AnvaPossiblePackage {
  quotes: AnvaQuote[];
  start_date?: string;
  payment_interval?: string;
  anva_collection_method_code?: number;
  discount_percentage?: number;
  discount?: number;
  total_premium?: number;
  // Maatwerk offerte tracking
  isMaatwerk?: boolean;
  maatwerkRequested?: boolean;
}

interface AnvaFlowsProductAnvaBasketStore {
  packages: Package[];
  insurances: AnvaQuote[];

  // setters
  setPackages: (packages: Package[]) => void;
  setInsurances: (insurances: AnvaQuote[]) => void;
  // getters
  getPackage: (packageCode: string) => Package | undefined;
  getInsurance: (anvaProductId: string, index: number) => AnvaQuote | undefined;

  // Add quotes to collections
  addToInsurances: (quote: AnvaQuote) => void;
  addToPackage: (packageCode: string, quote: AnvaQuote) => void;

  createPackage: (pack: AnvaPossiblePackage) => void;
  updatePackage: (packageCode: string, pack: Partial<Package>) => void;
  updateInsurance: (
    anvaProductId: string,
    index: number,
    quote: Partial<AnvaQuote>,
  ) => void;
  updateInsuranceInPackage: (
    packageCode: string,
    anvaProductId: string,
    index: number,
    quote: Partial<AnvaQuote>,
  ) => void;

  // Remove quotes from collections
  removeFromInsurances: (quoteAnvaProductId: string, index: number) => void;
  removeFromPackage: (
    packageCode: string,
    quoteAnvaProductId: string,
    index: number,
  ) => void;
  removePackage: (packageCode: string) => void;

  // Move quotes between collections
  moveFromInsuranceToPackage: (
    packageCode: string,
    quoteAnvaProductId: string,
    index: number,
  ) => void;
  moveFromPackageToInsurance: (
    packageCode: string,
    quoteAnvaProductId: string,
    index: number,
  ) => void;
  moveToAnotherPackage: (
    fromPackageCode: string,
    toPackageCode: string,
    quoteAnvaProductId: string,
    index: number,
  ) => void;

  // Validation
  isBasketValidated: boolean;
  setIsBasketValidated: (isValidated: boolean) => void;

  // Reset store
  reset: () => void;
}

const useAnvaFlowProductsBasketStore = create<AnvaFlowsProductAnvaBasketStore>(
  (set, get) => ({
    packages: [],
    insurances: [],

    getPackage: (packageCode: string) =>
      get().packages.find((pkg) => pkg.code === packageCode),
    getInsurance: (anvaProductId: string, index: number) =>
      get().insurances.find(
        (ins) => ins.anva_product_id === anvaProductId && ins.index === index,
      ),

    // Setters
    setPackages: (packages) => {
      set(() => ({
        packages,
        isBasketValidated: false,
      }));
    },
    setInsurances: (insurances) => {
      set(() => ({
        insurances,
        isBasketValidated: false,
      }));
    },

    // Add quotes to collections
    addToInsurances: (quote) =>
      set((state) => ({
        insurances: [...state.insurances, quote],
        isBasketValidated: false,
      })),

    addToPackage: (packageCode, quote) =>
      set((state) => {
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: [...newPackages[packageIndex].quotes, quote],
        };

        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),

    // Create a new package
    createPackage: (pack) =>
      set((state) => {
        // Check if package with this ID already exists
        if (state.packages.some((pkg) => pkg.code === pack.code)) return state;

        return {
          packages: [...state.packages, { ...pack, quotes: [] }],
          isBasketValidated: false,
        };
      }),
    updatePackage: (pkgCode, partialPkg) =>
      set((state) => {
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === pkgCode,
        );
        if (packageIndex === -1) return state;

        const currentPackage = state.packages[packageIndex];

        // Propagate certain package-level fields to all nested quotes to keep UI consistent
        const updatedQuotes = currentPackage.quotes.map((quote) => {
          const override: Partial<typeof quote> = {};

          if (partialPkg.anva_collection_method_code !== undefined) {
            override.anva_collection_method_code =
              partialPkg.anva_collection_method_code;
          }
          if (partialPkg.start_date !== undefined) {
            override.start_date = partialPkg.start_date;
          }
          if (partialPkg.payment_interval !== undefined) {
            override.payment_interval = partialPkg.payment_interval;
          }

          return { ...quote, ...override };
        });

        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...currentPackage,
          ...partialPkg,
          quotes: updatedQuotes,
        };

        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),
    updateInsuranceInPackage: (packageCode, anvaProductId, index, quote) =>
      set((state) => {
        const newPackages = [...state.packages];
        const pkgIndex = newPackages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        const pkg = newPackages[pkgIndex];
        if (!pkg) return state;
        const insuranceIndex = pkg?.quotes.findIndex(
          (ins) => ins.anva_product_id === anvaProductId && ins.index === index,
        );

        if (insuranceIndex === -1) return state;
        const newInsurances = [...pkg.quotes];
        newInsurances[insuranceIndex] = {
          ...newInsurances[insuranceIndex],
          ...quote,
        };
        newPackages[pkgIndex] = {
          ...newPackages[pkgIndex],
          quotes: newInsurances,
        };
        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),
    updateInsurance: (anvaProductId, index, quote) =>
      set((state) => {
        const insuranceIndex = state.insurances.findIndex(
          (ins) => ins.anva_product_id === anvaProductId && ins.index === index,
        );
        if (insuranceIndex === -1) return state;
        const newInsurances = [...state.insurances];
        newInsurances[insuranceIndex] = {
          ...newInsurances[insuranceIndex],
          ...quote,
        };
        return {
          insurances: newInsurances,
          isBasketValidated: false,
        };
      }),
    // Remove quotes from collections
    removeFromInsurances: (anvaProductId, index) =>
      set((state) => ({
        insurances: state.insurances.filter(
          (quote) =>
            !(quote.anva_product_id === anvaProductId && quote.index === index),
        ),
        isBasketValidated: false,
      })),

    removeFromPackage: (packageCode, anvaProductId, index) =>
      set((state) => {
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: newPackages[packageIndex].quotes.filter(
            (quote) =>
              !(
                quote.anva_product_id === anvaProductId && quote.index === index
              ),
          ),
        };
        // Remove package if it has no quotes left
        if (newPackages[packageIndex].quotes.length === 0) {
          newPackages.splice(packageIndex, 1);
        }
        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),

    removePackage: (packageCode) =>
      set((state) => ({
        packages: state.packages.filter((pkg) => pkg.code !== packageCode),
        isBasketValidated: false,
      })),

    // Move quotes between collections
    moveFromInsuranceToPackage: (packageCode, quoteAnvaProductId, index) =>
      set((state) => {
        // Find the quote in insurances
        const quote = state.insurances.find(
          (q) => q.anva_product_id === quoteAnvaProductId && q.index === index,
        );
        if (!quote) return state;

        // Find target package
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        // Remove from insurances
        const newInsurances = state.insurances.filter(
          (q) =>
            !(q.anva_product_id === quoteAnvaProductId && q.index === index),
        );

        // Add to package and only transfer collection method from quote to package if package doesn't have one
        const newPackages = [...state.packages];
        const targetPackage = newPackages[packageIndex];

        newPackages[packageIndex] = {
          ...targetPackage,
          quotes: [...targetPackage.quotes, quote],
          // Only transfer collection method from quote to package if package doesn't have one
          anva_collection_method_code:
            targetPackage.anva_collection_method_code ??
            quote.anva_collection_method_code ??
            undefined,
        };

        return {
          insurances: newInsurances,
          packages: newPackages,
          isBasketValidated: false,
        };
      }),

    moveFromPackageToInsurance: (packageCode, quoteAnvaProductId, index) =>
      set((state) => {
        // Find the package
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        const packageData = state.packages[packageIndex];

        // Find the quote in the package
        const quote = packageData.quotes.find(
          (q) => q.anva_product_id === quoteAnvaProductId && q.index === index,
        );
        if (!quote) return state;

        // Remove from package
        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: newPackages[packageIndex].quotes.filter(
            (q) =>
              !(q.anva_product_id === quoteAnvaProductId && q.index === index),
          ),
        };

        // Remove package if it has no quotes left
        if (newPackages[packageIndex].quotes.length === 0) {
          newPackages.splice(packageIndex, 1);
        }

        // Add to insurances with package-level collection method transferred
        const updatedQuote = {
          ...quote,
          // Transfer collection method from package to individual quote if package has one
          anva_collection_method_code:
            packageData.anva_collection_method_code ||
            quote.anva_collection_method_code,
          // Also transfer other package-level data that should move with the quote
          payment_interval:
            packageData.payment_interval || quote.payment_interval,
          start_date: packageData.start_date || quote.start_date,
        };
        const newInsurances = [...state.insurances, updatedQuote];

        return {
          packages: newPackages,
          insurances: newInsurances,
          isBasketValidated: false,
        };
      }),

    moveToAnotherPackage: (
      fromPackageCode,
      toPackageCode,
      quoteAnvaProductId,
      index,
    ) =>
      set((state) => {
        const fromPackageIndex = state.packages.findIndex(
          (pkg) => pkg.code === fromPackageCode,
        );
        const toPackageIndex = state.packages.findIndex(
          (pkg) => pkg.code === toPackageCode,
        );

        if (fromPackageIndex === -1 || toPackageIndex === -1) return state;

        // Find the quote in source package
        const quote = state.packages[fromPackageIndex].quotes.find(
          (q) => q.anva_product_id === quoteAnvaProductId && q.index === index,
        );
        if (!quote) return state;

        // Remove from source package
        const newPackages = [...state.packages];
        newPackages[fromPackageIndex] = {
          ...newPackages[fromPackageIndex],
          quotes: newPackages[fromPackageIndex].quotes.filter(
            (q) =>
              !(q.anva_product_id === quoteAnvaProductId && q.index === index),
          ),
        };

        // Add to target package
        newPackages[toPackageIndex] = {
          ...newPackages[toPackageIndex],
          quotes: [...newPackages[toPackageIndex].quotes, quote],
        };

        // Remove package if it has no quotes left
        if (newPackages[fromPackageIndex].quotes.length === 0) {
          newPackages.splice(fromPackageIndex, 1);
        }

        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),
    isBasketValidated: false,
    setIsBasketValidated: (isValidated) =>
      set(() => ({
        isBasketValidated: isValidated,
      })),

    // Reset store
    reset: () =>
      set(() => ({
        packages: [],
        insurances: [],
        isBasketValidated: false,
      })),
  }),
);

export default useAnvaFlowProductsBasketStore;
